import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { playerDetailsService } from "../api/services/playerDetails/playerDetailsService";
import {
  CreatePlayerDetailsRequest,
  UpdatePlayerDetailsRequest,
  PlayerDetailsListParams,
} from "../api/services/playerDetails/types";
import toast from "react-hot-toast";

export const usePlayerDetails = (params?: PlayerDetailsListParams) => {
  return useQuery({
    queryKey: ["player-details", params],
    queryFn: () => playerDetailsService.listPlayerDetails(params),
  });
};

export const usePlayerDetailsById = (id: string, enabled = true) => {
  return useQuery({
    queryKey: ["player-details", id],
    queryFn: () => playerDetailsService.getPlayerDetails(id),
    enabled: enabled && !!id,
  });
};

export const usePlayerDetailsByPlayerId = (
  playerId: number,
  enabled = true,
) => {
  return useQuery({
    queryKey: ["player-details-by-player", playerId],
    queryFn: () => playerDetailsService.getPlayerDetailsByPlayerId(playerId),
    enabled: enabled && !!playerId && playerId > 0,
    retry: false,
  });
};

export const useCreatePlayerDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePlayerDetailsRequest) =>
      playerDetailsService.createPlayerDetails(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["player-details"] });
      toast.success("Player details created successfully");
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(
        err.response?.data?.detail || "Failed to create player details",
      );
    },
  });
};

export const useUpdatePlayerDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdatePlayerDetailsRequest;
    }) => playerDetailsService.updatePlayerDetails(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["player-details"] });
      queryClient.invalidateQueries({ queryKey: ["player-details", id] });
      toast.success("Player details updated successfully");
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(
        err.response?.data?.detail || "Failed to update player details",
      );
    },
  });
};

export const useUpdatePlayerDetailsByPlayerId = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      playerId,
      data,
    }: {
      playerId: number;
      data: UpdatePlayerDetailsRequest;
    }) => playerDetailsService.updatePlayerDetailsByPlayerId(playerId, data),
    onSuccess: (_, { playerId }) => {
      queryClient.invalidateQueries({ queryKey: ["player-details"] });
      queryClient.invalidateQueries({
        queryKey: ["player-details-by-player", playerId],
      });
      toast.success("Player details updated successfully");
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(
        err.response?.data?.detail || "Failed to update player details",
      );
    },
  });
};

export const useDeletePlayerDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => playerDetailsService.deletePlayerDetails(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["player-details"] });
      toast.success("Player details deleted successfully");
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(
        err.response?.data?.detail || "Failed to delete player details",
      );
    },
  });
};
